import api from './api';
import { ApiResponse, unwrapApiResponse } from '@/utils/api-response.util';
import { toLocalIso } from '@/utils/datetime';

// Enums for workout properties
export enum WorkoutCategory {
  CARDIO = 'cardio',
  STRENGTH = 'strength',
  FLEXIBILITY = 'flexibility',
  SPORTS = 'sports',
  OTHER = 'other',
}

export enum MeasurementUnit {
  MINUTE = 'minute',
  REPETITION = 'repetition',
  KILOMETER = 'kilometer',
  SET = 'set',
  STEP = 'step',
  CALORIE = 'calorie',
  LEVEL = 'level',
  OTHER = 'other',
}

export type WorkoutSourceType = 'system' | 'user' | 'all' | 'legacy_exercise';
export type WorkoutSortBy = 'id' | 'category' | 'name';
export type SortOrder = 'ASC' | 'DESC';
export type Locale = 'uk' | 'en' | 'ru' | 'cs' | 'es' | 'pl' | 'be';

// Workout DTOs
export interface WorkoutResponseDto {
  id: number;
  name: string;
  category: WorkoutCategory;
  createdByUserId: number | null;
  measurementUnit: string; // Localized string
  rawMeasurementUnit: string; // Non-localized enum value
  metValue: number | null;
  metActivityId: number | null;
  defaultMeasurementValue: number | null;
  nameI18n: { [key in Locale]?: string };
  isStructured: boolean;
  isSystem: boolean; // Added isSystem
  usageCount: number; // Added usageCount
  availableTranslations: { [key in Locale]?: boolean }; // Added availableTranslations
  imageUrl?: string | null; // Added imageUrl
  externalLink?: string | null; // Added externalLink
  legacyId?: string | null; // Added legacyId
  legacyEntity?: 'exercise' | null; // Added legacyEntity
  legacyImg?: string | null; // Added legacyImg
}

export interface WorkoutSuggestion {
  id: number | null;
  name: string;
  category: string;
  measurementUnit?: string;
  rawMeasurementUnit?: string;
  metValue?: number | null;
  metActivityId?: number | null;
  defaultMeasurementValue?: number | null;
  isStructured: boolean;
  isSystem: boolean; // Added isSystem
  usageCount?: number; // Added usageCount
  imageUrl?: string | null; // Added imageUrl
  externalLink?: string | null; // Added externalLink
}

export interface PaginatedWorkoutsResponseDto {
  data: WorkoutResponseDto[];
  meta: {
    total: number;
    page: number;
    limit: number;
  };
}

// Query Parameters
export interface GetWorkoutsParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: WorkoutSortBy;
  sortOrder?: SortOrder;
  category?: WorkoutCategory;
  sourceType?: WorkoutSourceType;
  expertId?: number; // Changed from userId to expertId
  isStructured?: boolean; // Added isStructured filter
}

// Workout Intake Payload
export interface WorkoutIntakePayload {
  workoutId?: number;
  customName?: string;
  qty?: number;
  durationMinutes?: number;
  scheduledLdt: string; // local datetime string (no Z suffix)
  isCompleted?: boolean;
  calendarId?: number;

  // Fields for creating a custom workout on the fly
  metValue?: number;
  metActivityId?: number;
  externalLink?: string;
  details?: string;
  defaultMeasurementValue?: number; // Added defaultMeasurementValue
}

// DTO for creating a workout
export interface CreateWorkoutDto {
  nameI18n: { [key in Locale]?: string };
  category: WorkoutCategory;
  measurementUnit: MeasurementUnit; // Changed to enum
  metValue: number | null;
  metActivityId?: number | null;
  defaultMeasurementValue: number | null; // Added defaultMeasurementValue
  imageId?: number | null; // Added imageId
  externalLink?: string | null; // Added externalLink
  detailsI18n?: { [key in Locale]?: string }; // Added detailsI18n
}

// DTO for updating a workout
export interface UpdateWorkoutDto {
  nameI18n?: { [key in Locale]?: string }; // Made optional for partial update
  category?: WorkoutCategory; // Made optional
  measurementUnit?: MeasurementUnit; // Changed to enum, made optional
  metValue?: number | null;
  metActivityId?: number | null;
  defaultMeasurementValue?: number | null; // Added defaultMeasurementValue
  imageId?: number | null; // Added imageId
  externalLink?: string | null; // Added externalLink
  detailsI18n?: { [key in Locale]?: string }; // Added detailsI18n
}

export interface MetActivityRef {
  id: number;
  name: string;
  nameEn: string;
  metValue: number;
  compendiumType: string;
  categoryName: string;
}

export interface WorkoutIntakeDetailDto {
  id: number;
  name: string;
  category: WorkoutCategory;
  measurementUnit: string;
  rawMeasurementUnit: string;
  isStructured: boolean;
  isSystem: boolean;
  defaultMeasurementValue: number | null;
  imageUrl: string | null;
  externalLink: string | null;
  details: string | null;
  metValue: number | null;
  metActivityId: number | null;
  metActivity: MetActivityRef | null;
}

// DTO for merging workouts
export interface MergeWorkoutDto {
  sourceWorkoutId: number;
  targetWorkoutId: number;
}

class WorkoutService {
  private readonly BASE_URL = '/lib/workouts';
  private readonly INTAKE_URL = '/calendars/events/workout';
  private readonly CALENDAR_EVENT_URL = '/calendars/events'; // Base URL for calendar events

  async getWorkouts(params: GetWorkoutsParams): Promise<PaginatedWorkoutsResponseDto> {
    const response = await api.get<any>(`${this.BASE_URL}`, {
      params,
    });

    const body = response.data;
    if (body && body.success && Array.isArray(body.data)) {
      return {
        data: body.data,
        meta: body.meta,
      };
    }
    
    console.error('Unexpected API response structure for getWorkouts:', body);
    return { 
      data: [], 
      meta: { total: 0, page: 1, limit: params.limit || 10 } 
    };
  }
  
  async getWorkoutSuggestions(query: string, limit: number = 10): Promise<WorkoutSuggestion[]> {
    const params: GetWorkoutsParams = {
      search: query,
      limit: limit,
    };
    const response = await this.getWorkouts(params);

    if (!response || !Array.isArray(response.data)) {
      console.error('Invalid response structure from getWorkouts. "response.data" is not an array. Response:', response);
      return [];
    }
    
    return response.data.map(workout => ({
      id: workout.id,
      name: workout.name,
      category: workout.category,
      measurementUnit: workout.measurementUnit,
      rawMeasurementUnit: workout.rawMeasurementUnit,
      metValue: workout.metValue,
      metActivityId: workout.metActivityId,
      defaultMeasurementValue: workout.defaultMeasurementValue,
      isStructured: workout.isStructured,
      isSystem: workout.isSystem,
    }));
  }

  async createWorkoutIntake(payload: WorkoutIntakePayload, clientId?: string | number): Promise<any> {
    const url = clientId ? `/client/${clientId}/calendar/events/workout` : this.INTAKE_URL;
    const response = await api.post<ApiResponse<any>>(url, payload);
    return unwrapApiResponse<any>(response);
  }

  // New function to update a workout intake event
  async updateWorkoutIntake(eventId: number, payload: WorkoutIntakePayload): Promise<any> {
    const response = await api.put<ApiResponse<any>>(`${this.CALENDAR_EVENT_URL}/${eventId}/workout`, payload);
    return unwrapApiResponse<any>(response);
  }

  async createWorkout(payload: CreateWorkoutDto): Promise<WorkoutResponseDto> {
    const response = await api.post<ApiResponse<WorkoutResponseDto>>(`${this.BASE_URL}`, payload);
    return unwrapApiResponse<WorkoutResponseDto>(response);
  }

  async getWorkoutForEdit(id: number): Promise<WorkoutResponseDto> {
    const response = await api.get<ApiResponse<WorkoutResponseDto>>(`${this.BASE_URL}/${id}`);
    return unwrapApiResponse<WorkoutResponseDto>(response);
  }

  async getWorkoutDetail(id: number): Promise<WorkoutIntakeDetailDto> {
    const response = await api.get<ApiResponse<WorkoutIntakeDetailDto>>(`${this.BASE_URL}/${id}`);
    return unwrapApiResponse<WorkoutIntakeDetailDto>(response);
  }

  async updateWorkout(id: number, payload: UpdateWorkoutDto): Promise<WorkoutResponseDto> {
    const response = await api.patch<ApiResponse<WorkoutResponseDto>>(`${this.BASE_URL}/${id}`, payload);
    return unwrapApiResponse<WorkoutResponseDto>(response);
  }

  async deleteWorkout(id: number): Promise<void> {
    await api.delete<ApiResponse<void>>(`${this.BASE_URL}/${id}`);
  }

  async mergeWorkouts(payload: MergeWorkoutDto): Promise<{ removedWorkout: WorkoutResponseDto; targetWorkout: WorkoutResponseDto; updatedCalendarEvents: number }> {
    const response = await api.post<ApiResponse<{ removedWorkout: WorkoutResponseDto; targetWorkout: WorkoutResponseDto; updatedCalendarEvents: number }>>(`${this.BASE_URL}/merge`, payload);
    return unwrapApiResponse<{ removedWorkout: WorkoutResponseDto; targetWorkout: WorkoutResponseDto; updatedCalendarEvents: number }>(response);
  }
}

export const workoutService = new WorkoutService();

export const getWorkoutSuggestions = (query: string, limit?: number) =>
  workoutService.getWorkoutSuggestions(query, limit);

export const createWorkoutIntake = (payload: WorkoutIntakePayload, clientId?: string | number) =>
  workoutService.createWorkoutIntake(payload, clientId);

export const updateWorkoutIntake = (eventId: number, payload: WorkoutIntakePayload) =>
  workoutService.updateWorkoutIntake(eventId, payload);

/** Опції збереження події з модалки: `eventId` - редагування; `clientId` - експерт пише в календар клієнта. */
export interface SaveIntakeOptions {
  eventId?: number;
  clientId?: string | number;
}

/** Зберегти тренування з AddWorkoutEventModal: зі словника - workoutId + тривалість, власне - з MET/посиланням/деталями. */
export const saveWorkoutIntake = (data: any, { eventId, clientId }: SaveIntakeOptions = {}) => {
  const payload: WorkoutIntakePayload = {
    scheduledLdt: toLocalIso(data.scheduledAt),
    isCompleted: data.isCompleted,
    calendarId: data.calendarId,
    durationMinutes: data.durationMinutes,
    ...(data.workoutId
      ? { workoutId: data.workoutId }
      : {
          customName: data.customName ?? data.name,
          metValue: data.metValue,
          metActivityId: data.metActivityId,
          externalLink: data.externalLink,
          details: data.details,
          defaultMeasurementValue: data.defaultMeasurementValue,
        }),
  };
  return eventId ? workoutService.updateWorkoutIntake(eventId, payload) : workoutService.createWorkoutIntake(payload, clientId);
};
